import { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import * as XLSX from "xlsx";
import styles from "./Admin.module.css";

// ── Login ─────────────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleLogin = async () => {
    if (!password) return;
    setLoading(true); setError("");

    try {
      const res = await fetch("/.netlify/functions/get-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminPassword: password }),
      });

      if (res.status === 401) {
        setError("Invalid password.");
        setLoading(false);
        return;
      }

      if (!res.ok) throw new Error("Server error");

      const { orders } = await res.json();
      onLogin(password, orders);
    } catch (err) {
      setError("Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginBox}>
        <div className={styles.loginIcon}>🔐</div>
        <h2 className={styles.loginTitle}>Admin Panel</h2>
        <p className={styles.loginSub}>Shortly Life — Internal Dashboard</p>
        <div className={styles.loginForm}>
          <input
            className={styles.loginInput}
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoFocus
          />
          {error && <p className={styles.loginError}>⚠ {error}</p>}
          <button className={styles.loginBtn} onClick={handleLogin} disabled={loading}>
            {loading ? "Logging in…" : "Login →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Export to Excel ───────────────────────────────────────────────────────────
function exportToExcel(orders) {
  const rows = orders.map((o) => ({
    "Order ID":    o.orderId,
    "Name":        o.name,
    "Email":       o.email,
    "Phone":       o.phone,
    "Course":      o.courseTitle,
    "Amount (₹)":  o.amount,
    "Date":        o.purchasedAt
      ? new Date(o.purchasedAt).toLocaleString("en-IN", {
          day: "numeric", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "—",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();

  ws["!cols"] = [
    { wch: 16 }, { wch: 22 }, { wch: 30 },
    { wch: 14 }, { wch: 35 }, { wch: 12 }, { wch: 22 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Orders");
  XLSX.writeFile(wb, `ShortlyLife_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function AdminDashboard({ orders, adminPassword, onLogout, onRefresh }) {
  const [search, setSearch]     = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh(adminPassword);
    setRefreshing(false);
  };

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    return (
      o.orderId.toLowerCase().includes(q)     ||
      o.name.toLowerCase().includes(q)        ||
      o.email.toLowerCase().includes(q)       ||
      o.phone.toLowerCase().includes(q)       ||
      o.courseTitle.toLowerCase().includes(q)
    );
  });

  const totalRevenue = orders.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const uniqueUsers  = new Set(orders.map((o) => o.email)).size;
  const todayOrders  = orders.filter((o) => {
    if (!o.purchasedAt) return false;
    return new Date(o.purchasedAt).toDateString() === new Date().toDateString();
  }).length;

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <div className={styles.dashHeader}>
        <div>
          <h1 className={styles.dashTitle}>Shortly Life</h1>
          <p className={styles.dashSub}>Orders Dashboard</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className={styles.refreshBtn} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "🔄 Refresh"}
          </button>
          <button className={styles.logoutBtn} onClick={onLogout}>Logout</button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        {[
          { icon: "🛒", label: "Total Orders",   value: orders.length },
          { icon: "👥", label: "Unique Buyers",  value: uniqueUsers },
          { icon: "📅", label: "Today's Orders", value: todayOrders },
          { icon: "₹",  label: "Total Revenue",  value: `₹${totalRevenue.toLocaleString("en-IN")}` },
        ].map((s, i) => (
          <div key={i} className={styles.statCard}>
            <span className={styles.statIcon}>{s.icon}</span>
            <div>
              <strong>{s.value}</strong>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          className={styles.searchInput}
          placeholder="Search by name, email, order ID, course…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className={styles.exportBtn}
          onClick={() => exportToExcel(filtered)}
          disabled={filtered.length === 0}
        >
          ⬇ Export to Excel ({filtered.length})
        </button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className={styles.emptyBox}>
          <span>📭</span>
          <p>{search ? "No orders match your search." : "No orders yet."}</p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Course</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr key={i}>
                  <td><code className={styles.orderId}>{o.orderId}</code></td>
                  <td>{o.name}</td>
                  <td className={styles.emailCell}>{o.email}</td>
                  <td>{o.phone}</td>
                  <td className={styles.courseCell}>{o.courseTitle}</td>
                  <td><strong>₹{o.amount}</strong></td>
                  <td className={styles.dateCell}>{fmtDate(o.purchasedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [adminPassword, setAdminPassword] = useState("");
  const [orders, setOrders]               = useState(null);

  const handleLogin = (password, fetchedOrders) => {
    setAdminPassword(password);
    setOrders(fetchedOrders);
  };

  const handleLogout = () => {
    setAdminPassword("");
    setOrders(null);
  };

  const handleRefresh = async (password) => {
    try {
      const res = await fetch("/.netlify/functions/get-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminPassword: password }),
      });
      const { orders: fresh } = await res.json();
      setOrders(fresh);
    } catch (err) {
      console.error("Refresh failed:", err.message);
    }
  };

  if (orders === null) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <AdminDashboard
      orders={orders}
      adminPassword={adminPassword}
      onLogout={handleLogout}
      onRefresh={handleRefresh}
    />
  );
}